import { ClearOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Skeleton } from 'antd'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import api from '~/services/api'
import history from '~/services/history'

import New from './New'
import PendingInspectionModal from './PendingInspection'
import { ActionsBar, Container, ReturnsTable, SearchControls, SearchInput, StatusTag } from './styles'

const statusColors = {
  'Received RB': '#b2fab4',
  Returning: '#FA8D8D',
  'Received CBI': '#FBE6A8',
}

export default function Returns({ location }) {
  const inputSearch = useRef('')

  const [returnList, setReturnList] = useState([
    {
      order: {
        customer: {},
      },
    },
  ])
  const [rmaData, setRmaData] = useState({})
  const [rmaIdParam, setRmaIdParam] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeFilter, setActiveFilter] = useState('')
  const [open, setOpen] = useState(false)
  const [pendingInspectionOpen, setPendingInspectionOpen] = useState(false)

  const loadReturns = useCallback(async (filter = '') => {
    try {
      setLoading(true)
      if (filter) {
        const returnsList = await api.get(`/returns?filter=${encodeURIComponent(filter)}`)
        if (returnsList.data.error) {
          toast.error(returnsList.data.error)
          return false
        }
        setReturnList(returnsList.data)
        return true
      }

      const returnsList = await api.get('/returns?status=open')
      setReturnList(returnsList.data)
      return true
    } catch (error) {
      toast.error('Error loading RMAs. Contact RBMS administrator.')
      return false
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadReturns()
  }, [loadReturns])

  const handleEdit = async (returnId) => {
    const returnData = await api.get(`/returns/${returnId}`)

    const returnInfo = returnData.data
    const { data: orderData } = await api.get(`/orders/${returnInfo.order_id}`)

    returnInfo.id = returnId
    returnInfo.items_returned = returnInfo.items_returned.split(',')
    returnInfo.customer = orderData.customer.name
    returnInfo.company = orderData?.customer?.company
    returnInfo.store = orderData.store.name
    returnInfo.order_date = orderData.order_date
    returnInfo.order_items = orderData.order_items
    returnInfo.checklist = orderData.checklist

    setRmaData(returnInfo)
    setOpen(true)
  }

  useEffect(() => {
    const { search } = location
    const params = new URLSearchParams(search)
    const id = params.get('id')
    if (id) {
      setRmaIdParam(id)
      handleEdit(id)
    }
  }, []) //eslint-disable-line

  const handleClose = async (shouldReload = false) => {
    setOpen(false)
    setRmaIdParam(null)
    setRmaData({})
    history.replace('returns')

    if (shouldReload) {
      await loadReturns(activeFilter)
    }
  }

  const handleOpen = () => {
    setOpen(true)
    setRmaData({})
  }

  const handleSearch = useCallback(
    async (filter) => {
      const normalizedFilter = filter ? filter.trim().toUpperCase() : ''
      const success = await loadReturns(normalizedFilter)
      if (success) {
        setActiveFilter(normalizedFilter)
      }
    },
    [loadReturns] // eslint-disable-line
  )

  const handleClearSearch = useCallback(async () => {
    if (inputSearch.current) {
      inputSearch.current.input.value = ''
    }
    setActiveFilter('')
    await loadReturns('')
  }, [loadReturns])

  const tableColumns = [
    {
      title: 'RMA',
      dataIndex: 'id',
      key: 'id',
      render: (value, row) => (loading ? <Skeleton active title={false} paragraph={{ rows: 1, width: '70%' }} /> : value || row.id),
    },
    {
      title: 'Order #',
      dataIndex: 'order_num',
      key: 'order_num',
      render: (value) => (loading ? <Skeleton active title={false} paragraph={{ rows: 1, width: '70%' }} /> : value),
    },
    {
      title: 'Return Date',
      key: 'createdAt',
      render: (_, row) => (loading ? <Skeleton active title={false} paragraph={{ rows: 1, width: '80%' }} /> : moment(row.createdAt).format('MMMM D, YYYY')),
    },
    {
      title: 'Order Date',
      key: 'order_date',
      render: (_, row) =>
        loading ? <Skeleton active title={false} paragraph={{ rows: 1, width: '80%' }} /> : moment(row.order?.order_date).format('MMMM D, YYYY'),
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, row) => {
        if (loading) {
          return <Skeleton active title={false} paragraph={{ rows: 1, width: '90%' }} />
        }
        return row.order?.customer?.company ? `${row.order?.customer?.name} (${row.order?.customer?.company})` : row.order?.customer?.name || ''
      },
    },
    {
      title: 'Address',
      key: 'address',
      render: (_, row) => {
        if (loading) {
          return <Skeleton active title={false} paragraph={{ rows: 1, width: '95%' }} />
        }
        return `${row.order?.customer?.street1 || ''}, ${row.order?.customer?.city || ''}, ${row.order?.customer?.state || ''}, ${
          row.order?.customer?.country || ''
        }`
      },
    },
    {
      title: 'Tracking Code',
      key: 'tracking',
      render: (_, row) => {
        if (loading) {
          return <Skeleton active title={false} paragraph={{ rows: 1, width: '85%' }} />
        }

        if (row.tracking && row.tracking.substring(0, 2) === '1Z') {
          return (
            <a rel="noopener noreferrer" target="_blank" href={`https://www.ups.com/track?loc=en_ca&tracknum=${row.tracking.replace(/\s/g, '')}`}>
              {row.tracking}
            </a>
          )
        }

        return row.tracking
      },
    },
    {
      title: 'Tracking Last update',
      key: 'latestTrackingUpdate',
      render: (_, row) =>
        loading ? (
          <Skeleton active title={false} paragraph={{ rows: 1, width: '90%' }} />
        ) : (
          <StatusTag $background={row.latestTrackingUpdate && row.latestTrackingUpdate?.toUpperCase().includes('DELIVERED') ? '#b2fab4' : '#FBE6A8'}>
            <span style={{ whiteSpace: 'nowrap' }}>{row.latestTrackingUpdate}</span>
          </StatusTag>
        ),
    },
    {
      title: 'Tracking Status',
      key: 'tracking_status',
      render: (_, row) =>
        loading ? (
          <Skeleton active title={false} paragraph={{ rows: 1, width: '80%' }} />
        ) : (
          <StatusTag $background={statusColors[row.tracking_status]}>{row.tracking_status}</StatusTag>
        ),
    },
    {
      title: 'Return Status',
      dataIndex: 'return_status',
      key: 'return_status',
      render: (value) => (loading ? <Skeleton active title={false} paragraph={{ rows: 1, width: '80%' }} /> : value),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => (loading ? <Skeleton active title={false} paragraph={{ rows: 1, width: '75%' }} /> : value),
    },
  ]

  const tableData = loading
    ? Array.from({ length: 6 }, (_, index) => ({
        key: `skeleton-${index}`,
      }))
    : returnList

  return (
    <Container>
      <ActionsBar>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpen}>
          Add
        </Button>

        <SearchControls>
          <SearchInput
            placeholder="Search"
            ref={inputSearch}
            onBlur={(e) => {
              e.target.value = e.target.value.trim().toUpperCase()
            }}
            onPressEnter={(e) => {
              handleSearch(e.target.value)
            }}
            suffix={<SearchOutlined onClick={() => handleSearch(inputSearch.current?.input?.value)} style={{ cursor: 'pointer' }} />}
          />

          {activeFilter && (
            <Button icon={<ClearOutlined />} onClick={handleClearSearch}>
              Clear Search
            </Button>
          )}
        </SearchControls>

        <Button onClick={() => setPendingInspectionOpen(true)}>RMA Technician Inspection</Button>
      </ActionsBar>

      <ReturnsTable
        columns={tableColumns}
        dataSource={tableData}
        pagination={false}
        rowKey={(record) => record.key || record.id}
        scroll={{ x: 1400 }}
        onRow={(record) => ({
          onDoubleClick: () => {
            if (!loading) {
              handleEdit(record.id)
            }
          },
          style: loading
            ? {}
            : {
                cursor: 'pointer',
              },
        })}
      />

      <New open={open} handleClose={handleClose} rma={rmaData} rmaIdParam={rmaIdParam} />

      <PendingInspectionModal open={pendingInspectionOpen} onClose={() => setPendingInspectionOpen(false)} />
    </Container>
  )
}
